import _ from 'lodash'
import delay from 'delay'

import FormControl from './form-control'

export default {

    props: {
        autoFill: {
            type: Boolean,
            default: false,
        },
        autoFillDisable: {
            type: Boolean,
            default: false,
        },
        autoFillDelay: {
            type: Number,
            default: 250,
        },
        tabstop: {
            type: Boolean,
            default: true,
        },
        tabindex: {
            type: Number,
            default: null,
        },
        value: {
            type: [ String, Number, Boolean ],
            default: null,
        },
    },

    mixins: [
        FormControl,
    ],

    data() {
        return {
            autoFillCheckActive: this.autoFill,
            autoFillCheckAttempts: 0,
            inputTriggers: false,
            internalValue: this.value,
        }
    },

    watch: {

        value(newValue) {
            this.internalValue = newValue
        },
    },

    computed: {

        tabIndex() {
            return this.tabindex || this.tabstop ? 0 : -1
        },
    },

    mounted() {
        this.autoFillCheckActive = this.autoFill
        if (this.autoFill && !this.autoFillDisable) this.checkForAutoFill(this.autoFillDelay)
        if (this.autoFillDisable) this.clearAutoFill(this.autoFillDelay)
    },

    unmounted() {
        this.autoFillCheckActive = false
    },

    methods: {

        async checkForAutoFill(delayPeriod) {
            if (this.autoFillCheckActive) {
                this.autoFillCheckAttempts++
                if (this.autoFillCheckAttempts > 20)
                    delayPeriod = 1000
                if (this.autoFillCheckAttempts > 40) {
                    this.autoFillCheckActive = false
                } else {
                    await delay(delayPeriod)
                    const el = this.$refs[this.def]
                    if (el && el.value && el.value.length && el.value !== this.internalValue) {
                        this.internalValue = el.value
                        this.$emit('input', this.internalValue)
                        this.$emit('change', { target: { value: el.value } })
                        this.autoFillCheckActive = false
                    }

                    if (this.autoFillCheckActive)
                        await this.checkForAutoFill(delayPeriod)
                }
            }
        },

        async clearAutoFill(delayPeriod) {
            await this.checkForAutoFill(delayPeriod)
            await delay(delayPeriod * 4)
            this.internalValue = null
            this.$emit('input', this.internalValue)
            this.$emit('change', { target: { value: null } })
        },

        /**
         * Formats the output of event values. This can be overridden to format for specific control types.
         *
         * @param {any} value
         * @returns {any} The formatted output according to the control.
         */
        formatEventOutput(value) {
            return value
        },

        /**
         * Event that fires on change of the control value.
         *
         * Debouncing: This is debounced slower to ensure that change fires after input so it can trigger an input
         * if input events are not triggered by the browser to maintain browser event consistency.
         *
         * @param {Object} ev The event data.
         */
        onChange: _.debounce(function(ev) {
            /* eslint-disable no-invalid-this */
            this.$emit('change', ev)
            if (!this.inputTriggers) {
                this.internalValue = this.formatEventOutput(ev.target.value)
                this.$emit('input', this.internalValue)
            }
            /* eslint-enable no-invalid-this */
        }, 600),

        /**
         * Event that fires on inputing into a control.
         *
         * Debouncing: This is debounced to minimise triggers for an average typing speed.
         * Browser Note: Some browsers (EdgeHTML) do not trigger input events for some controls. Where this is
         * the case the 'change' event will fire an input event if input has not fired.
         *
         * @param {Object} ev The event data.
         */
        onInput: _.debounce(function(ev) {
            /* eslint-disable no-invalid-this */
            this.inputTriggers = true
            this.internalValue = this.formatEventOutput(ev.target.value)
            this.$emit('input', this.internalValue)
            /* eslint-enable no-invalid-this */
        }, 500),


        /**
         * Event that fires on blur.
         *
         * Debouncing: This is debounced to minimise triggers for an average typing speed.
         * Browser Note: Some browsers (EdgeHTML) do not trigger input events for some controls. Where this is
         * the case the 'change' event will fire an input event if input has not fired.
         */
        onBlur: _.debounce(function() {
            /* eslint-disable no-invalid-this */
            this.$emit('blur')
            /* eslint-enable no-invalid-this */
        }, 700),
    },
}
